import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMetaClient, useRemote } from '../../meta/hooks.ts';
import type { NewsItem } from '../../meta/types.ts';
import { useFlag } from '../../meta/liveStore.ts';
import { Badge, Panel, ScreenFrame } from '../../ui/index.ts';
import { useDateFormat } from '../account/common.tsx';
import { Failed, GatePanel, Page, useMetaGate } from './common.tsx';
import styles from './Meta.module.css';

/** `/news`: what the team has to say, the pinned first, in the language of the app. */
export function NewsScreen() {
  const { t, i18n } = useTranslation('meta');
  const { date } = useDateFormat();
  const gate = useMetaGate();
  const enabled = useFlag('news');
  const meta = useMetaClient();
  const load = useMemo(
    () => (meta && gate === 'ready' && enabled ? () => meta.news(20) : null),
    [meta, gate, enabled],
  );
  const news = useRemote<NewsItem[]>(load);
  const lang = i18n.language === 'it' ? 'it' : 'en';
  return (
    <ScreenFrame title={t('news.title')} backTo="/">
      <Page>
        {gate !== 'ready' ? <GatePanel gate={gate} /> : null}
        {gate === 'ready' && !enabled ? (
          <Panel>
            <p className={styles['note']}>{t('news.off')}</p>
          </Panel>
        ) : null}
        {news.status === 'error' ? <Failed onRetry={news.reload} /> : null}
        {gate === 'ready' && enabled && news.status === 'loading' ? (
          <GatePanel gate="loading" />
        ) : null}
        {news.data && news.data.length === 0 ? (
          <Panel>
            <p className={styles['note']}>{t('news.empty')}</p>
          </Panel>
        ) : null}
        {news.data?.map((item) => (
          <Panel key={item.id} as="article" title={item.title[lang]}>
            <div className={styles['stack']}>
              <div className={styles['row']}>
                <span className={styles['note']}>{date(item.publishedAt)}</span>
                {item.pinned ? <Badge tone="amber">{t('news.pinned')}</Badge> : null}
              </div>
              {item.body[lang].split('\n').map((paragraph, i) => (
                // The text is the team's own: paragraphs are its lines.
                <p key={`${item.id}-${String(i)}`} className={styles['paragraph']}>
                  {paragraph}
                </p>
              ))}
            </div>
          </Panel>
        ))}
      </Page>
    </ScreenFrame>
  );
}
